import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import SideBar from "../../components/SideBar/SideBar";
import TopNav from "../../components/TopNav/TopNav";
import api from "../../services/api";
import Button from "../../components/Buttons/Button";
import "./VenueSync.scss";

function VenueSync() {
  const navigate = useNavigate();

  // 24-hour localStorage cache so sync results don't vanish on page reload
  const SYNC_CACHE_KEY = "venue-sync-cache-v2";
  const SYNC_TTL_MS = 24 * 60 * 60 * 1000;
  const readCache = () => {
    try {
      const raw = localStorage.getItem(SYNC_CACHE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (Date.now() - new Date(parsed.lastSync).getTime() > SYNC_TTL_MS) return null;
      return parsed;
    } catch { return null; }
  };
  const writeCache = (payload) => {
    try { localStorage.setItem(SYNC_CACHE_KEY, JSON.stringify({ ...payload, lastSync: new Date().toISOString() })); } catch {}
  };

  const cached = readCache();
  const [candidates, setCandidates] = useState(cached?.candidates || []);
  const [closedVenues, setClosedVenues] = useState(cached?.closedVenues || []);
  const [pendingVenues, setPendingVenues] = useState([]);
  const [lastSync, setLastSync] = useState(cached ? new Date(cached.lastSync) : null);

  const [syncLoading, setSyncLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [syncError, setSyncError] = useState(null);

  // Which candidates + closed are selected for bulk action
  const [selCand, setSelCand] = useState(new Set());
  const [selClosed, setSelClosed] = useState(new Set());

  // Load pending review venues on mount (cheap Mongo query, no Google cost)
  useEffect(() => {
    fetchPending();
  }, []);

  async function fetchPending() {
    try {
      const { data } = await api.get("/admin/venue-sync/pending-review");
      setPendingVenues(data?.data || []);
    } catch (err) {
      console.error("[fetchPending]", err);
    }
  }

  async function runSync() {
    const confirmed = window.confirm(
      "Find new Miami venues?\n\n" +
      "This will:\n" +
      "• Scan Google for new nightlife venues not in Zona\n" +
      "• Check existing venues for permanent closures\n\n" +
      "Cost: about $2–5 (well inside the free credit)."
    );
    if (!confirmed) return;

    setSyncLoading(true);
    setSyncError(null);
    try {
      const [candRes, closedRes] = await Promise.all([
        api.get("/admin/venue-sync/candidates"),
        api.get("/admin/venue-sync/closed"),
      ]);
      const c = candRes.data?.data || [];
      const cl = closedRes.data?.data || [];
      setCandidates(c);
      setClosedVenues(cl);
      setSelCand(new Set(c.map((x) => x.place_id)));
      setSelClosed(new Set(cl.map((x) => x._id)));
      setLastSync(new Date());
      writeCache({ candidates: c, closedVenues: cl });
      toast.success(`Found ${c.length} new, ${cl.length} closed`);
    } catch (err) {
      setSyncError(err?.response?.data?.message || err?.message || "Sync failed");
    } finally {
      setSyncLoading(false);
    }
  }

  async function addSelectedCandidates() {
    if (selCand.size === 0) return toast.warn("Select at least one venue");
    setActionLoading(true);
    try {
      const { data } = await api.post("/admin/venue-sync/add", {
        place_ids: Array.from(selCand),
      });
      toast.success(`Added ${data.created_count} venues to Pending Review`);
      const added = new Set(data.created.map((c) => c.place_id));
      const remaining = candidates.filter((c) => !added.has(c.place_id));
      setCandidates(remaining);
      setSelCand(new Set());
      writeCache({ candidates: remaining, closedVenues });
      // Refresh pending so new venues show up below
      await fetchPending();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add venues");
    } finally {
      setActionLoading(false);
    }
  }

  async function deactivateSelectedClosed() {
    if (selClosed.size === 0) return toast.warn("Select at least one venue");
    setActionLoading(true);
    try {
      const { data } = await api.post("/admin/venue-sync/deactivate", {
        venue_ids: Array.from(selClosed),
      });
      toast.success(`Marked ${data.modified} venues as closed`);
      const remaining = closedVenues.filter((v) => !selClosed.has(v._id));
      setClosedVenues(remaining);
      setSelClosed(new Set());
      writeCache({ candidates, closedVenues: remaining });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to deactivate venues");
    } finally {
      setActionLoading(false);
    }
  }

  // One-click approve a pending venue — flips status:pending_review → active
  async function approveVenue(venueId) {
    setActionLoading(true);
    try {
      await api.put(`/venues/${venueId}`, { status: "active" });
      toast.success("Venue is live");
      setPendingVenues((prev) => prev.filter((v) => v._id !== venueId));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to approve");
    } finally {
      setActionLoading(false);
    }
  }

  async function dismissCandidate(placeId) {
    // Just remove from the local list — nothing stored
    const remaining = candidates.filter((c) => c.place_id !== placeId);
    setCandidates(remaining);
    setSelCand((prev) => {
      const next = new Set(prev);
      next.delete(placeId);
      return next;
    });
    writeCache({ candidates: remaining, closedVenues });
  }

  const toggleCand = (id) => setSelCand((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleClosed = (id) => setSelClosed((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const hasSynced = lastSync != null;
  const nothingToShow = hasSynced && candidates.length === 0 && closedVenues.length === 0 && pendingVenues.length === 0;

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="venue-sync">
            {/* Hero + Run sync */}
            <div className="venue-sync__hero">
              <div>
                <h1>Venue Ops</h1>
                <p className="venue-sync__subtitle">
                  {hasSynced
                    ? `Last synced ${lastSync.toLocaleString('en-US', { timeZone: 'America/New_York' })}`
                    : "Click the button to find new Miami venues and check which ones have closed."}
                </p>
              </div>
              <Button
                label={syncLoading ? "Searching Google…" : hasSynced ? "Re-run sync" : "Find new venues"}
                onClick={runSync}
                gradientButton
                textColor="#FFFFFF"
                className="zona-pill-btn"
                disabled={syncLoading}
              />
            </div>

            {syncError && <div className="venue-sync__error">{syncError}</div>}

            {/* Section 1: Pending Review — always show first if there are any */}
            {pendingVenues.length > 0 && (
              <Section
                title="Needs your approval"
                subtitle={`${pendingVenues.length} venue${pendingVenues.length === 1 ? "" : "s"} drafted by AI. Review and approve to make them live.`}
                tone="pending"
              >
                {pendingVenues.map((v) => (
                  <PendingRow
                    key={v._id}
                    venue={v}
                    onApprove={() => approveVenue(v._id)}
                    onEdit={() => navigate(`/business/edit/${v._id}`)}
                    disabled={actionLoading}
                  />
                ))}
              </Section>
            )}

            {/* Section 2: Candidates to add */}
            {candidates.length > 0 && (
              <Section
                title="New venues to add"
                subtitle={`${candidates.length} Miami nightlife venue${candidates.length === 1 ? "" : "s"} found on Google that aren't in Zona yet. Review the list and add the ones you want.`}
                tone="add"
                bulkAction={
                  <Button
                    label={actionLoading ? "Adding…" : `Add ${selCand.size} to Zona`}
                    onClick={addSelectedCandidates}
                    gradientButton
                    textColor="#FFFFFF"
                    className="zona-pill-btn"
                    disabled={actionLoading || selCand.size === 0}
                  />
                }
              >
                {candidates.map((c) => (
                  <CandidateRow
                    key={c.place_id}
                    venue={c}
                    selected={selCand.has(c.place_id)}
                    onToggle={() => toggleCand(c.place_id)}
                    onDismiss={() => dismissCandidate(c.place_id)}
                  />
                ))}
              </Section>
            )}

            {/* Section 3: Closures to deactivate */}
            {closedVenues.length > 0 && (
              <Section
                title="Venues that look closed"
                subtitle={`${closedVenues.length} active venue${closedVenues.length === 1 ? "" : "s"} Google marked as permanently closed. Verify each via the Maps link before confirming.`}
                tone="closed"
                bulkAction={
                  <Button
                    label={actionLoading ? "Closing…" : `Mark ${selClosed.size} as closed`}
                    onClick={deactivateSelectedClosed}
                    gradientButton
                    textColor="#FFFFFF"
                    className="zona-pill-btn"
                    disabled={actionLoading || selClosed.size === 0}
                  />
                }
              >
                {closedVenues.map((v) => (
                  <ClosedRow
                    key={v._id}
                    venue={v}
                    selected={selClosed.has(v._id)}
                    onToggle={() => toggleClosed(v._id)}
                  />
                ))}
              </Section>
            )}

            {/* Empty states */}
            {!hasSynced && pendingVenues.length === 0 && (
              <div className="venue-sync__empty-hero">
                <div className="venue-sync__empty-icon">⌕</div>
                <h3>Ready to find venues</h3>
                <p>Click "Find new venues" above. Google will return Miami nightlife spots that aren't in Zona yet, plus any that may have closed.</p>
              </div>
            )}

            {nothingToShow && (
              <div className="venue-sync__empty-hero">
                <div className="venue-sync__empty-icon">✓</div>
                <h3>You're all caught up</h3>
                <p>Zona's venue list matches Google. Nothing new to add, nothing closed. Check back in a week.</p>
              </div>
            )}
          </section>
        </article>
      </div>
    </main>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Section wrapper

function Section({ title, subtitle, tone, bulkAction, children }) {
  return (
    <section className={`venue-sync__section venue-sync__section--${tone}`}>
      <header className="venue-sync__section-head">
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
        {bulkAction}
      </header>
      <div className="venue-sync__rows">{children}</div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Row components

function PendingRow({ venue, onApprove, onEdit, disabled }) {
  return (
    <div className="venue-sync__pending-row">
      {venue.image_urls?.[0] ? (
        <img src={venue.image_urls[0]} alt={venue.name} className="venue-sync__pending-img" />
      ) : (
        <div className="venue-sync__pending-img venue-sync__pending-img--empty">no photo</div>
      )}
      <div className="venue-sync__pending-body">
        <div className="venue-sync__pending-head">
          <div>
            <div className="venue-sync__row-name">{venue.name}</div>
            <div className="venue-sync__row-addr">{venue.address}</div>
          </div>
          <span className={`venue-sync__cat venue-sync__cat--${venue.category}`}>{venue.category}</span>
        </div>
        {venue.description && <p className="venue-sync__pending-desc">{venue.description}</p>}
        <div className="venue-sync__pending-chips">
          {(venue.tags || []).map((t) => <span key={`t-${t}`} className="venue-sync__chip">{t}</span>)}
          {(venue.music || []).map((m) => <span key={`m-${m}`} className="venue-sync__chip venue-sync__chip--music">♪ {m}</span>)}
          {(venue.dress || []).map((d) => <span key={`d-${d}`} className="venue-sync__chip venue-sync__chip--dress">{d}</span>)}
        </div>
        <div className="venue-sync__pending-actions">
          <button onClick={onApprove} disabled={disabled} className="venue-sync__approve-btn">
            ✓ Approve &amp; publish
          </button>
          <button onClick={onEdit} disabled={disabled} className="venue-sync__edit-link">
            Edit first →
          </button>
        </div>
      </div>
    </div>
  );
}

function CandidateRow({ venue, selected, onToggle, onDismiss }) {
  return (
    <div className={`venue-sync__row ${selected ? "is-selected" : ""}`} onClick={onToggle}>
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className="venue-sync__checkbox venue-sync__checkbox--success"
      />
      <div className="venue-sync__row-body">
        <div className="venue-sync__row-name">{venue.name}</div>
        <div className="venue-sync__row-addr">{venue.address}</div>
        <div className="venue-sync__row-meta">
          {venue.rating ? <span>★ {venue.rating}</span> : null}
          {venue.reviews ? <span>{venue.reviews} reviews</span> : null}
          {venue.types?.includes("night_club") && <span className="venue-sync__chip">club</span>}
          {venue.types?.includes("bar") && !venue.types?.includes("night_club") && <span className="venue-sync__chip">bar</span>}
        </div>
      </div>
      <a
        href={venue.google_maps_url}
        target="_blank"
        rel="noreferrer"
        className="venue-sync__maps-link"
        onClick={(e) => e.stopPropagation()}
      >
        Maps
      </a>
      <button
        className="venue-sync__dismiss-btn"
        onClick={(e) => { e.stopPropagation(); onDismiss(); }}
        title="Not interested"
      >
        ✕
      </button>
    </div>
  );
}

function ClosedRow({ venue, selected, onToggle }) {
  return (
    <div className={`venue-sync__row ${selected ? "is-selected" : ""}`} onClick={onToggle}>
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className="venue-sync__checkbox venue-sync__checkbox--danger"
      />
      <div className="venue-sync__row-body">
        <div className="venue-sync__row-name">{venue.name}</div>
        <div className="venue-sync__row-addr">{venue.address}</div>
      </div>
      <a
        href={venue.google_maps_url}
        target="_blank"
        rel="noreferrer"
        className="venue-sync__maps-link"
        onClick={(e) => e.stopPropagation()}
      >
        Verify on Maps
      </a>
    </div>
  );
}

export default VenueSync;
